import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Search, Edit2, Trash2, Phone, User, FileText, X, MapPin, Plus, ChevronRight, Scissors, CheckCircle2, Calendar } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO, addDays } from "date-fns";

// ─── Article & Measurement Definitions (mirrors CreateSlipPage) ───
const SHIRT_MEASUREMENTS = ["Length", "Chest", "Waist", "Hip", "Shoulder", "Sleeves", "Neck"];
const KURTA_MEASUREMENTS = ["Length", "Chest", "Waist", "Hip", "Shoulder", "Sleeves", "Neck", "Mori"];
const COAT_MEASUREMENTS = ["Length", "Chest", "Waist", "Hip", "Shoulder", "Sleeves", "Neck", "Jacket Length", "Cross Back", "Mori"];
const PRINCE_COAT_MEASUREMENTS = ["Length", "Chest", "Waist", "Hip", "Shoulder", "Sleeves", "Neck", "Cross Back", "Mori"];
const PANT_MEASUREMENTS = ["Length", "Waist", "Hip", "Thigh", "Knee", "Calf", "Bottom", "U"];
const PAJAMA_MEASUREMENTS = ["Length", "Waist", "Hip", "Thigh", "Bottom", "U"];
const LOWER_BODY_MEASUREMENTS = ["Length", "Waist", "Hip", "Thigh", "Knee", "Calf", "Bottom", "U"];

const CLOTHING_TYPES = [
  { value: "shirt", label: "Shirt", measurements: SHIRT_MEASUREMENTS },
  { value: "coat", label: "Coat", measurements: COAT_MEASUREMENTS },
  { value: "prince_coat", label: "Prince Coat", measurements: PRINCE_COAT_MEASUREMENTS },
  { value: "sherwani", label: "Sherwani", measurements: COAT_MEASUREMENTS },
  { value: "kurta", label: "Kurta", measurements: KURTA_MEASUREMENTS },
  { value: "short_kurta", label: "Short Kurta", measurements: KURTA_MEASUREMENTS },
  { value: "pathani", label: "Pathani", measurements: KURTA_MEASUREMENTS },
  { value: "waistcoat", label: "Waistcoat", measurements: SHIRT_MEASUREMENTS },
  { value: "modi_jacket", label: "Modi Jacket", measurements: SHIRT_MEASUREMENTS },
  { value: "blazer", label: "Blazer", measurements: COAT_MEASUREMENTS },
  { value: "pant", label: "Pant", measurements: PANT_MEASUREMENTS },
  { value: "trouser", label: "Trouser", measurements: PANT_MEASUREMENTS },
  { value: "pajama", label: "Pajama", measurements: PAJAMA_MEASUREMENTS },
  { value: "shalwar", label: "Shalwar", measurements: LOWER_BODY_MEASUREMENTS },
  { value: "police_uniform", label: "Police Uniform", measurements: SHIRT_MEASUREMENTS },
];

const emptyItem = () => ({ clothing_type: "", quantity: 1, measurements: {}, notes: "" });

// ─── Quick Slip Dialog Component ───
function QuickSlipDialog({ customer, open, onClose, onCreated }) {
  const [savedMeasurements, setSavedMeasurements] = useState({});
  const [loadingMeas, setLoadingMeas] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState("");
  const [items, setItems] = useState([emptyItem()]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !customer) return;
    setItems([emptyItem()]);
    setDeliveryDate("");
    setSaving(false);
    setLoadingMeas(true);
    api.get(`/customers/${customer.id}/saved-measurements`)
      .then(r => setSavedMeasurements(r.data))
      .catch(() => setSavedMeasurements({}))
      .finally(() => setLoadingMeas(false));
  }, [open, customer]);

  // When saved data arrives, retroactively fill any already-selected article types
  useEffect(() => {
    if (!Object.keys(savedMeasurements).length) return;
    setItems(prev => prev.map(item => {
      if (!item.clothing_type) return item;
      const saved = savedMeasurements[item.clothing_type];
      if (!saved) return item;
      const clothingDef = CLOTHING_TYPES.find(c => c.value === item.clothing_type);
      const keys = clothingDef?.measurements || [];
      const updatedMeasurements = { ...item.measurements };
      keys.forEach(k => {
        if (!updatedMeasurements[k] && saved.measurements?.[k]) {
          updatedMeasurements[k] = saved.measurements[k];
        }
      });
      return { ...item, measurements: updatedMeasurements, notes: item.notes || saved.notes || "" };
    }));
  }, [savedMeasurements]);

  const setArticleType = (idx, type) => {
    const clothingDef = CLOTHING_TYPES.find(c => c.value === type);
    const savedData = savedMeasurements[type] || {};
    const saved = savedData.measurements || {};
    const savedNotes = savedData.notes || "";
    // Pre-fill with saved measurements and notes, leave blank if not saved
    const measurements = {};
    (clothingDef?.measurements || []).forEach(k => {
      measurements[k] = saved[k] !== undefined ? saved[k] : "";
    });
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, clothing_type: type, measurements, notes: savedNotes } : it));
  };

  const setMeasurement = (idx, key, val) => {
    setItems(prev => prev.map((it, i) => i === idx
      ? { ...it, measurements: { ...it.measurements, [key]: val } }
      : it
    ));
  };

  const handleSubmit = async () => {
    if (!deliveryDate) { toast.error("Please select a delivery date"); return; }
    const invalid = items.find(it => !it.clothing_type);
    if (invalid) { toast.error("Please select an article type for all items"); return; }
    setSaving(true);
    try {
      const res = await api.post("/slips", {
        customer_id: customer.id,
        delivery_date: deliveryDate,
        items: items.map(it => ({
          clothing_type: it.clothing_type,
          quantity: it.quantity,
          measurements: it.measurements,
          notes: it.notes || ""
        }))
      });
      toast.success("Slip created successfully!");
      onCreated(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create slip");
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-card border-border rounded-sm">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl flex items-center gap-2">
            <Scissors className="w-5 h-5 gold-text" />
            Quick New Slip — {customer?.name}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground text-sm">
            {loadingMeas ? "Loading saved measurements..." : `${Object.keys(savedMeasurements).length} article(s) with saved measurements`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Delivery Date */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Delivery Date</Label>
            <Input
              type="date"
              value={deliveryDate}
              min={format(addDays(new Date(), 1), "yyyy-MM-dd")}
              onChange={e => setDeliveryDate(e.target.value)}
              className="bg-background border-border rounded-sm h-9 text-sm w-48"
              data-testid="quick-slip-delivery-date"
            />
          </div>

          {/* Article Items */}
          {items.map((item, idx) => {
            const clothingDef = CLOTHING_TYPES.find(c => c.value === item.clothing_type);
            const hasSaved = item.clothing_type && !!savedMeasurements[item.clothing_type];
            const measKeys = clothingDef?.measurements || [];

            return (
              <div key={idx} className="border border-border rounded-sm p-4 space-y-4 bg-background/50">
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Article {idx + 1}</span>
                  {idx > 0 && (
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-red-400"
                      onClick={() => setItems(prev => prev.filter((_, i) => i !== idx))}>
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>

                {/* Article type + quantity row */}
                <div className="flex gap-3">
                  <div className="flex-1">
                    <Label className="text-xs text-muted-foreground mb-1 block">Article Type</Label>
                    <Select value={item.clothing_type} onValueChange={v => setArticleType(idx, v)}>
                      <SelectTrigger className="bg-background border-border rounded-sm h-9 text-sm" data-testid={`article-type-${idx}`}>
                        <SelectValue placeholder="Select article..." />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        {CLOTHING_TYPES.map(ct => (
                          <SelectItem key={ct.value} value={ct.value} className="text-sm">
                            <span className="flex items-center gap-2">
                              {ct.label}
                              {savedMeasurements[ct.value] && (
                                <CheckCircle2 className="w-3 h-3 text-green-400" />
                              )}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-24">
                    <Label className="text-xs text-muted-foreground mb-1 block">Quantity</Label>
                    <Input
                      type="number" min={1} max={20}
                      value={item.quantity}
                      onChange={e => setItems(prev => prev.map((it, i) => i === idx ? { ...it, quantity: parseInt(e.target.value) || 1 } : it))}
                      className="bg-background border-border rounded-sm h-9 text-sm text-center"
                      data-testid={`article-qty-${idx}`}
                    />
                  </div>
                </div>

                {/* Saved measurement indicator */}
                {item.clothing_type && (
                  <div className={`text-xs px-2 py-1 rounded-sm inline-flex items-center gap-1.5 ${hasSaved ? 'bg-green-500/10 text-green-400' : 'bg-yellow-500/10 text-yellow-400'}`}>
                    {hasSaved ? <CheckCircle2 className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                    {hasSaved ? "Measurements & notes pre-filled from last order" : "No saved measurements — please enter below"}
                  </div>
                )}

                {/* Measurements grid */}
                {measKeys.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {measKeys.map(key => (
                      <div key={key}>
                        <Label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block">{key}</Label>
                        <Input
                          value={item.measurements[key] || ""}
                          onChange={e => setMeasurement(idx, key, e.target.value)}
                          placeholder="—"
                          className="bg-background border-border rounded-sm h-8 text-sm text-center"
                          data-testid={`meas-${idx}-${key}`}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Notes */}
                {item.clothing_type && (
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">Notes (optional)</Label>
                    <Input
                      value={item.notes || ""}
                      onChange={e => setItems(prev => prev.map((it, i) => i === idx ? { ...it, notes: e.target.value } : it))}
                      placeholder="Special instructions..."
                      className="bg-background border-border rounded-sm h-8 text-sm"
                    />
                  </div>
                )}
              </div>
            );
          })}

          {/* Add another article */}
          <Button variant="outline" size="sm" onClick={() => setItems(prev => [...prev, emptyItem()])}
            className="w-full border-dashed border-border text-muted-foreground hover:text-foreground rounded-sm h-9"
            data-testid="add-another-article">
            <Plus className="w-3.5 h-3.5 mr-2" /> Add Another Article
          </Button>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-sm border-border">Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} className="btn-maroon rounded-sm" data-testid="quick-slip-submit">
            {saving ? "Creating..." : "Create Slip"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CustomersPage() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", address: "" });
  const [saving, setSaving] = useState(false);
  
  // Customer detail view
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSlips, setCustomerSlips] = useState([]);
  const [loadingSlips, setLoadingSlips] = useState(false);

  // Quick Slip dialog
  const [quickSlipOpen, setQuickSlipOpen] = useState(false);

  // Ref for auto-scrolling to customer detail panel
  const detailRef = useRef(null);

  // Scroll to detail panel when customer is selected
  useEffect(() => {
    if (selectedCustomer && detailRef.current) {
      setTimeout(() => {
        detailRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    }
  }, [selectedCustomer]);

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await api.get("/customers", { params: search ? { search } : {} });
      setCustomers(res.data);
    } catch { toast.error("Failed to load customers"); }
    setLoading(false);
  }, [search]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  const fetchCustomerSlips = async (customer) => {
    setSelectedCustomer(customer);
    setLoadingSlips(true);
    try {
      const res = await api.get("/slips", { params: { customer_id: customer.id } });
      setCustomerSlips(res.data);
    } catch {
      toast.error("Failed to load customer slips");
      setCustomerSlips([]);
    }
    setLoadingSlips(false);
  };

  const closeCustomerDetail = () => {
    setSelectedCustomer(null);
    setCustomerSlips([]);
  };

  const openEdit = (c, e) => {
    e?.stopPropagation();
    setEditing(c);
    setForm({ name: c.name, phone: c.phone, address: c.address || "" });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.phone) { toast.error("Name and phone are required"); return; }
    setSaving(true);
    try {
      await api.put(`/customers/${editing.id}`, {
        name: form.name,
        phone: form.phone,
        address: form.address || null
      });
      toast.success("Customer updated");
      setDialogOpen(false);
      fetchCustomers();
      // Update selected customer if editing
      if (selectedCustomer?.id === editing.id) {
        setSelectedCustomer({ ...selectedCustomer, name: form.name, phone: form.phone, address: form.address });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
    setSaving(false);
  };

  const handleDelete = async (c, e) => {
    e?.stopPropagation();
    if (!window.confirm(`Delete customer "${c.name}"?`)) return;
    try {
      await api.delete(`/customers/${c.id}`);
      toast.success("Customer deleted");
      fetchCustomers();
      if (selectedCustomer?.id === c.id) {
        closeCustomerDetail();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const filtered = customers;

  const statusLabels = { pending: "Pending", sent_to_worker: "In Progress", product_ready: "Ready", received: "Delivered" };

  // Calculate total articles for customer
  const getTotalArticles = () => {
    return customerSlips.reduce((total, slip) => {
      return total + (slip.items?.reduce((sum, item) => sum + (item.quantity || 1), 0) || 0);
    }, 0);
  };

  return (
    <div data-testid="customers-page" className="space-y-4 md:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 animate-fade-in-up">
        <div>
          <h1 className="font-serif text-2xl md:text-3xl font-semibold text-foreground">Customers</h1>
          <p className="text-muted-foreground text-sm mt-1">Manage your clientele</p>
        </div>
        <Button onClick={() => navigate("/slips/new")} className="btn-maroon rounded-sm gap-2 w-full sm:w-auto">
          <FileText className="w-4 h-4" /> New Slip (Add Customer)
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search by name, phone, address, or slip number..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 bg-card border-border rounded-sm h-11"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Customers List */}
        <div className={selectedCustomer ? "lg:col-span-1" : "lg:col-span-3"}>
          {/* Mobile Cards View */}
          <div className="block md:hidden space-y-3">
            {loading ? (
              [...Array(3)].map((_, i) => (
                <div key={i} className="h-20 bg-card rounded-sm animate-pulse" />
              ))
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">No customers found</div>
            ) : filtered.map((c) => (
              <Card 
                key={c.id} 
                className={`luxury-card rounded-sm cursor-pointer hover:border-[rgba(212,175,55,0.3)] transition-colors ${selectedCustomer?.id === c.id ? 'border-[rgba(212,175,55,0.5)]' : ''}`}
                onClick={() => fetchCustomerSlips(c)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-foreground font-medium">{c.name}</p>
                        <ChevronRight className="w-3 h-3 text-muted-foreground" />
                      </div>
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                        <Phone className="w-3 h-3" /> {c.phone}
                      </p>
                      {c.address && <p className="text-xs text-muted-foreground truncate mt-1">{c.address}</p>}
                      {c.last_order_date && (
                        <p className="text-[10px] text-muted-foreground/60 flex items-center gap-1 mt-1">
                          <Calendar className="w-2.5 h-2.5" />
                          Last order: {format(parseISO(c.last_order_date), "dd MMM yyyy")}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                      <Button variant="ghost" size="icon" onClick={(e) => openEdit(c, e)} className="h-8 w-8 text-muted-foreground">
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={(e) => handleDelete(c, e)} className="h-8 w-8 text-muted-foreground hover:text-red-400">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Desktop Table View */}
          <Card className="luxury-card rounded-sm hidden md:block">
            <CardContent className="p-0">
              <Table className="luxury-table">
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Name</TableHead>
                    {!selectedCustomer && (
                      <>
                        <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Phone</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Address</TableHead>
                      </>
                    )}
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    [...Array(3)].map((_, i) => (
                      <TableRow key={i}><TableCell colSpan={selectedCustomer ? 2 : 4}><div className="h-8 bg-muted/20 rounded animate-pulse" /></TableCell></TableRow>
                    ))
                  ) : filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={selectedCustomer ? 2 : 4} className="text-center py-12 text-muted-foreground">
                        No customers found. Create a new slip to add customers.
                      </TableCell>
                    </TableRow>
                  ) : filtered.map((c) => (
                    <TableRow 
                      key={c.id} 
                      className={`border-border cursor-pointer hover:bg-white/[0.02] transition-colors ${selectedCustomer?.id === c.id ? 'bg-[rgba(212,175,55,0.05)] border-l-2 border-l-[#D4AF37]' : ''}`}
                      onClick={() => fetchCustomerSlips(c)}
                    >
                      <TableCell className="text-sm text-foreground font-medium">
                        <div className="flex items-center gap-2">
                          {c.name}
                          <ChevronRight className="w-3 h-3 text-muted-foreground" />
                        </div>
                        {c.last_order_date && (
                          <div className="text-[10px] text-muted-foreground/60 flex items-center gap-1 mt-0.5">
                            <Calendar className="w-2.5 h-2.5" />
                            {format(parseISO(c.last_order_date), "dd MMM yyyy")}
                          </div>
                        )}
                      </TableCell>
                      {!selectedCustomer && (
                        <>
                          <TableCell className="text-sm text-muted-foreground">{c.phone}</TableCell>
                          <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate" title={c.address || ""}>
                            {c.address || "-"}
                          </TableCell>
                        </>
                      )}
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={(e) => openEdit(c, e)} className="h-8 w-8 text-muted-foreground hover:text-foreground">
                            <Edit2 className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={(e) => handleDelete(c, e)} className="h-8 w-8 text-muted-foreground hover:text-red-400">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        {/* Customer Detail Panel */}
        {selectedCustomer && (
          <div ref={detailRef} className="lg:col-span-2 space-y-4">
            {/* Customer Info Card */}
            <Card className="luxury-card rounded-sm">
              <CardHeader className="pb-3 flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-serif text-xl">{selectedCustomer.name}</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1 flex items-center gap-2">
                    <Phone className="w-3 h-3" /> {selectedCustomer.phone}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={(e) => openEdit(selectedCustomer, e)} className="rounded-sm border-border h-8 text-xs">
                    <Edit2 className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  <Button variant="ghost" size="icon" onClick={closeCustomerDetail} className="h-8 w-8 text-muted-foreground">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {selectedCustomer.address && (
                  <div className="flex items-start gap-2 mb-4 text-sm text-muted-foreground">
                    <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>{selectedCustomer.address}</span>
                  </div>
                )}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-background rounded-sm p-3 border border-border text-center">
                    <p className="text-2xl font-serif font-bold text-foreground">{customerSlips.length}</p>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total Slips</p>
                  </div>
                  <div className="bg-background rounded-sm p-3 border border-border text-center">
                    <p className="text-2xl font-serif font-bold gold-text">{getTotalArticles()}</p>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total Articles</p>
                  </div>
                  <div className="bg-background rounded-sm p-3 border border-border text-center">
                    <p className="text-2xl font-serif font-bold text-green-400">
                      {customerSlips.filter(s => s.status === 'received').length}
                    </p>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Completed</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Customer Slips */}
            <Card className="luxury-card rounded-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-serif text-base flex items-center gap-2">
                    <FileText className="w-4 h-4 gold-text" />
                    Order History
                  </CardTitle>
                  <Button 
                    onClick={() => setQuickSlipOpen(true)} 
                    size="sm" 
                    className="btn-maroon rounded-sm h-8 text-xs"
                    data-testid="new-article-btn"
                  >
                    <Scissors className="w-3 h-3 mr-1" /> New Article
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingSlips ? (
                  <div className="space-y-2">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="h-16 bg-muted/20 rounded animate-pulse" />
                    ))}
                  </div>
                ) : customerSlips.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground text-sm">
                    No slips found for this customer
                  </p>
                ) : (
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {customerSlips.map((slip) => (
                      <div 
                        key={slip.id} 
                        className="bg-background rounded-sm p-4 border border-border hover:border-[rgba(212,175,55,0.3)] cursor-pointer transition-colors"
                        onClick={() => navigate(`/slips/${slip.id}`)}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="text-sm font-medium gold-text">{slip.slip_number}</p>
                            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                              <Calendar className="w-3 h-3" />
                              {slip.created_at ? format(parseISO(slip.created_at), "dd MMM yyyy") : ""}
                            </p>
                          </div>
                          <Badge variant="outline" className={`text-[10px] rounded-sm status-${slip.status}`}>
                            {statusLabels[slip.status]}
                          </Badge>
                        </div>
                        {/* Articles List */}
                        <div className="mt-3 space-y-1">
                          <div className="flex flex-wrap gap-2">
                            {slip.items?.map((item, idx) => (
                              <span 
                                key={idx} 
                                className="text-xs bg-[rgba(114,47,55,0.15)] text-[#D4AF37] px-2 py-1 rounded-sm border border-[rgba(114,47,55,0.2)]"
                              >
                                {item.clothing_type?.replace(/_/g, " ")} × {item.quantity || 1}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-3 pt-2 border-t border-border">
                          <p className="text-[10px] text-muted-foreground">
                            Delivery: {slip.delivery_date ? format(parseISO(slip.delivery_date), "dd MMM yyyy") : "N/A"}
                          </p>
                          <p className="text-xs text-foreground font-medium">
                            {slip.items?.reduce((sum, item) => sum + (item.quantity || 1), 0) || 0} articles
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-lg">Edit Customer</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">Update customer information</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Full name"
                  className="pl-10 bg-background border-border rounded-sm h-10"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Phone</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="Phone number"
                  className="pl-10 bg-background border-border rounded-sm h-10"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Address</Label>
              <textarea
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Address (optional)"
                className="w-full min-h-[60px] px-3 py-2 bg-background border border-border rounded-sm text-sm text-foreground placeholder:text-muted-foreground resize-none outline-none focus:border-[rgba(212,175,55,0.5)]"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm border-border">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="btn-maroon rounded-sm">
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Slip Dialog */}
      <QuickSlipDialog
        customer={selectedCustomer}
        open={quickSlipOpen}
        onClose={() => setQuickSlipOpen(false)}
        onCreated={(newSlip) => {
          setQuickSlipOpen(false);
          navigate(`/slips/${newSlip.id}`);
        }}
      />
    </div>
  );
}
